import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Play, RotateCcw, Trash2, Terminal, Code2, AlertCircle, Info, Clock, Shield, CheckCircle, Moon, Sun, Type } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import { getSupabaseUrl } from '../supabase/client';

// 直接导入 Monaco Editor，避免生成 chunk 文件
import Editor from '@monaco-editor/react';

const defaultCode = `// 欢迎使用 C++ 在线编程环境
// 在这里编写你的 C++ 代码，点击"运行"即可编译执行

#include <iostream>
using namespace std;

int main() {
    cout << "Hello, C++!" << endl;
    
    // 示例：计算斐波那契数列
    int n = 10;
    cout << "斐波那契数列前" << n << "项:" << endl;
    
    long long a = 0, b = 1;
    for (int i = 0; i < n; i++) {
        cout << a << " ";
        long long next = a + b;
        a = b;
        b = next;
    }
    cout << endl;
    
    return 0;
}
`;

export default function CppPlayground() {
  const [code, setCode] = useState(defaultCode);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [executionTime, setExecutionTime] = useState(0);
  const [compileTime, setCompileTime] = useState(0);
  const [theme, setTheme] = useState<'vs-dark' | 'light'>('vs-dark');
  const [fontSize, setFontSize] = useState(14);
  const [showSettings, setShowSettings] = useState(false);
  const editorRef = useRef<any>(null);

  const handleRun = useCallback(async () => {
    setIsRunning(true);
    setOutput('');
    setError('');
    setExecutionTime(0);
    setCompileTime(0);

    const startTime = performance.now();

    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/code-grading`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'cpp_compile', code }),
      });

      const result = await response.json();
      const elapsed = Math.round(performance.now() - startTime);

      if (!response.ok) {
        setError(result.error || '编译执行失败');
        setCompileTime(result.compileTime || 0);
      } else {
        setCompileTime(result.compileTime || 0);
        setExecutionTime(result.executionTime || 0);
        if (result.compileError) {
          setError(result.compileError);
        } else {
          setOutput(result.output || '(程序执行完成，无输出)');
        }
      }
    } catch (err: any) {
      setError('请求失败: ' + (err.message || '网络连接问题'));
    } finally {
      setIsRunning(false);
    }
  }, [code]);

  const handleReset = useCallback(() => {
    setCode(defaultCode);
    setOutput('');
    setError('');
    setExecutionTime(0);
    setCompileTime(0);
  }, []);

  const handleClear = useCallback(() => {
    setCode('');
    setOutput('');
    setError('');
    setExecutionTime(0);
    setCompileTime(0);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'vs-dark' ? 'light' : 'vs-dark');
  }, []);

  const increaseFontSize = useCallback(() => {
    setFontSize(prev => Math.min(prev + 2, 24));
  }, []);

  const decreaseFontSize = useCallback(() => {
    setFontSize(prev => Math.max(prev - 2, 10));
  }, []);

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-4 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">C++</span>
                </div>
                <div>
                  <h3 className="font-bold text-white">代码编辑器</h3>
                  <p className="text-xs text-gray-400">GCC C++17 标准支持</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleClear}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  清空
                </button>
                <button
                  onClick={handleReset}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <RotateCcw className="w-4 h-4" />
                  重置
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <Type className="w-4 h-4" />
                    设置
                  </button>
                  {showSettings && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute right-0 top-full mt-2 bg-gray-800 rounded-lg shadow-lg p-3 min-w-[200px] z-10"
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-300">主题</span>
                          <button
                            onClick={toggleTheme}
                            className="p-1.5 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            {theme === 'vs-dark' ? (
                              <Moon className="w-4 h-4 text-gray-300" />
                            ) : (
                              <Sun className="w-4 h-4 text-yellow-400" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-300">字体大小</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={decreaseFontSize}
                              className="px-2 py-1 bg-gray-700 rounded hover:bg-gray-600 text-xs text-white"
                            >
                              A-
                            </button>
                            <span className="text-sm text-white w-6 text-center">{fontSize}</span>
                            <button
                              onClick={increaseFontSize}
                              className="px-2 py-1 bg-gray-700 rounded hover:bg-gray-600 text-xs text-white"
                            >
                              A+
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
                <button
                  onClick={handleRun}
                  disabled={isRunning}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ml-2"
                >
                  <Play className="w-4 h-4" />
                  {isRunning ? '编译中...' : '运行代码'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-4">
              <div className="col-span-3 border-r border-gray-200">
                  <Editor
                    height="600px"
                    language="cpp"
                    theme={theme}
                    value={code}
                    onChange={(value) => setCode(value || '')}
                    onMount={handleEditorDidMount}
                    options={{
                      fontSize: fontSize,
                      minimap: { enabled: true },
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      tabSize: 2,
                      insertSpaces: true,
                      formatOnPaste: true,
                      formatOnType: true,
                      suggestOnTriggerCharacters: true,
                      acceptSuggestionOnEnter: 'on',
                      quickSuggestions: true,
                      snippetSuggestions: 'top',
                    }}
                  />
              </div>

              <div className="col-span-1 bg-gray-900 h-[600px] overflow-hidden">
                <div className="flex items-center justify-between p-3 border-b border-gray-700 bg-gray-800">
                  <div className="flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-white">输出结果</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {compileTime > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>编译 {compileTime}ms</span>
                      </div>
                    )}
                    {executionTime > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>运行 {executionTime}ms</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 h-[calc(600px-49px)] overflow-auto">
                  <AnimatePresence mode="wait">
                    {isRunning ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center h-full"
                      >
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-gray-400 text-xs">编译执行中...</p>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {error ? (
                          <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
                            <div className="flex items-center gap-1.5 text-red-400 mb-1.5">
                              <AlertCircle className="w-3.5 h-3.5" />
                              <span className="font-medium text-xs">错误</span>
                            </div>
                            <pre className="text-xs text-red-300 whitespace-pre-wrap font-mono">
                              {error}
                            </pre>
                          </div>
                        ) : output ? (
                          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                            <div className="flex items-center gap-1.5 text-green-400 mb-1.5">
                              <CheckCircle className="w-3.5 h-3.5" />
                              <span className="font-medium text-xs">成功</span>
                            </div>
                            <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                              {output}
                            </pre>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-gray-500">
                            <div className="text-center">
                              <Terminal className="w-8 h-8 mx-auto mb-2 opacity-30" />
                              <p className="text-xs">点击运行代码</p>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Code2 className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">C++ 版本</h3>
              </div>
              <p className="text-xs text-gray-600">支持 C++17 标准，使用 GCC 编译器在云端编译执行</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 text-orange-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">运行限制</h3>
              </div>
              <p className="text-xs text-gray-600">编译时间限制 10 秒，运行时间限制 1 秒，内存限制 64MB</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Info className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">使用提示</h3>
              </div>
              <p className="text-xs text-gray-600">支持代码补全和语法高亮，首次编译可能需要等待环境初始化</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
